import { Site, WebsiteEvent } from "../resources/modelTypes";
import MapMarker from "./MapMarker";
import { css } from "@emotion/react";
import GoogleMap from "google-map-react";
import { fitBounds } from "google-map-react/utils";
import isEqual from "lodash/isEqual";
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type LatLng = {
  lat: number;
  lng: number;
};

type Size = {
  height: number;
  width: number;
};

// MapLocation accepts WebsiteEvent's position: number (sort order) but the Map
// component ignores it and geocodes addresses to get LatLng coordinates
export type MapLocation = WebsiteEvent & {
  formattedAddress: string;
};

// After geocoding, we add the LatLng position for map display
type GeocodedLocation = MapLocation & {
  geoPosition: LatLng;
};

type MapProps = {
  apiKey: string;
  focusedLocationIndex: number | null;
  focusLocation: (index: number | null) => void;
  locations: MapLocation[];
  onLocationsGeocoded: (hasValidLocations: boolean) => void;
  site: Site;
};

// Google Maps types (inline to avoid requiring @types/google.maps)
type GoogleLatLng = {
  lat: () => number;
  lng: () => number;
};

type GoogleLatLngBounds = {
  extend: (latLng: LatLng) => void;
  getNorthEast: () => GoogleLatLng;
  getSouthWest: () => GoogleLatLng;
};

type GoogleGeocodeResult = {
  geometry: {
    location: GoogleLatLng;
  };
};

type GoogleGeocoder = {
  geocode: (
    request: { address: string },
    callback: (results: GoogleGeocodeResult[] | null, status: string) => void,
  ) => void;
};

type GoogleMapsAPI = {
  Geocoder: new () => GoogleGeocoder;
  LatLngBounds: new () => GoogleLatLngBounds;
  MapOptions?: Record<string, unknown>;
};

type GoogleMapsApiLoadedParams = {
  map: unknown;
  maps: GoogleMapsAPI;
  ref: Element | null;
};

type GoogleMapChangeParams = {
  center: LatLng;
  size: Size;
  zoom: number;
};

type DistancePosition = {
  positionX: number;
  positionY: number;
};

const defaultCenter: LatLng = {
  lat: 41.850033,
  lng: -87.6500523,
};

const defaultZoom = 10;

const createOptions = () => ({
  fullscreenControl: false,
  scrollwheel: false,
});

const distanceToMouse = (
  { positionX, positionY }: DistancePosition,
  { positionX: mouseX, positionY: mouseY }: DistancePosition,
): number => {
  const adjustedPositionY = positionY - 25; // Vertical offset since our markers are entirely above the actual location point.

  return Math.sqrt(
    (positionX - mouseX) * (positionX - mouseX) +
      (adjustedPositionY - mouseY) * (adjustedPositionY - mouseY),
  );
};

const noValidLocationsStyles = css`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
`;

/**
 * Geocodes a single location address to get its LatLng coordinates
 */
const geocodeLocation = (
  geocoder: GoogleGeocoder,
  location: MapLocation,
): Promise<GeocodedLocation | null> => {
  return new Promise((resolve) => {
    geocoder.geocode(
      {
        address: location.formattedAddress,
      },
      (results, status) => {
        if (status === "OK" && results && results[0]) {
          const geoPosition: LatLng = {
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng(),
          };

          const result: GeocodedLocation = {
            ...location,
            geoPosition,
          };

          resolve(result);
        } else {
          resolve(null);
        }
      },
    );
  });
};

/**
 * Map component that displays event locations on a Google Map
 */
const Map = ({
  apiKey,
  focusedLocationIndex,
  focusLocation,
  locations,
  onLocationsGeocoded,
}: MapProps) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const [center, setCenter] = useState<LatLng>(defaultCenter);
  const [hasValidLocations, setHasValidLocations] = useState(true);
  const [markers, setMarkers] = useState<(GeocodedLocation | null)[]>([]);
  const [size, setSize] = useState<Size>({ height: 0, width: 0 });
  const [zoom, setZoom] = useState(defaultZoom);

  // Track previous focusedLocationIndex to detect changes
  const prevFocusedLocationIndexRef = useRef<number | null>(null);

  // Center on focused marker when focusedLocationIndex changes
  useEffect(() => {
    if (
      focusedLocationIndex != null &&
      focusedLocationIndex !== prevFocusedLocationIndexRef.current
    ) {
      const marker = markers[focusedLocationIndex];
      if (marker?.geoPosition) {
        // Use setTimeout to avoid synchronous setState in effect
        setTimeout(() => {
          setCenter(marker.geoPosition);
        }, 0);
      }
    }
    prevFocusedLocationIndexRef.current = focusedLocationIndex;
  }, [focusedLocationIndex, markers]);

  const getBoundsForLocations = useCallback(
    (
      maps: GoogleMapsAPI,
      geocodedLocations: GeocodedLocation[],
    ): { center: LatLng; zoom: number } => {
      const bounds = new maps.LatLngBounds();

      geocodedLocations.forEach(({ geoPosition }) => {
        if (geoPosition) {
          bounds.extend(geoPosition);
        }
      });

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      const corners = {
        ne: {
          lat: ne.lat(),
          lng: ne.lng(),
        },
        sw: {
          lat: sw.lat(),
          lng: sw.lng(),
        },
      };

      if (
        corners.ne.lat === corners.sw.lat &&
        corners.ne.lng === corners.sw.lng
      ) {
        // We have a single location so fitBounds from google-map-react won't work, just return the location as center
        return {
          center: geocodedLocations[0].geoPosition,
          zoom: 12,
        };
      }

      const node = mapContainerRef.current;
      if (!node) {
        return {
          center: defaultCenter,
          zoom: defaultZoom,
        };
      }

      const { height, width } = node.getBoundingClientRect();

      return fitBounds(corners, {
        height: height + 2 * MapMarker.MARKER_TOP,
        width: width + 2 * MapMarker.MARKER_LEFT,
      });
    },
    [],
  );

  const handleGoogleApiLoaded = useCallback(
    ({ maps }: GoogleMapsApiLoadedParams): void => {
      const geocoder = new maps.Geocoder();

      const promises = locations.map((location) =>
        geocodeLocation(geocoder, location),
      );

      // Trigger window resize so scroll bounds are updated
      window.dispatchEvent(new Event("resize"));

      Promise.all(promises).then((geocodedLocations) => {
        const validLocations = geocodedLocations.filter(
          (loc): loc is GeocodedLocation => loc !== null,
        );

        if (validLocations.length === 0) {
          setHasValidLocations(false);
          onLocationsGeocoded(false);
        } else {
          setHasValidLocations(true);
          setMarkers(geocodedLocations);
          onLocationsGeocoded(true);

          setTimeout(() => {
            const { center: newCenter, zoom: newZoom } = getBoundsForLocations(
              maps,
              validLocations,
            );
            setCenter(newCenter);
            setZoom(newZoom);
          }, 500);
        }
      });
    },
    [getBoundsForLocations, locations, onLocationsGeocoded],
  );

  const handleChange = useCallback(
    ({
      center: newCenter,
      size: newSize,
      zoom: newZoom,
    }: GoogleMapChangeParams): void => {
      setCenter(isEqual(newSize, size) ? newCenter : center);
      setSize(newSize);
      setZoom(newZoom);
    },
    [center, size],
  );

  const handleChildClick = useCallback(
    (_key: string, childProps: { index: number }): void => {
      const { index } = childProps;
      focusLocation(index);
    },
    [focusLocation],
  );

  const hashedLocations: Record<string, MapLocation[]> = useMemo(() => {
    const result: Record<string, MapLocation[]> = {};

    locations.forEach((marker) => {
      if (marker.hasPhysicalAddress) {
        if (result[marker.formattedAddress]) {
          result[marker.formattedAddress].push(marker);
        } else {
          result[marker.formattedAddress] = [marker];
        }
      }
    });

    return result;
  }, [locations]);

  if (!hasValidLocations) {
    return <div css={noValidLocationsStyles}>No valid locations</div>;
  }

  return (
    <div ref={mapContainerRef} style={{ height: "100%", width: "100%" }}>
      <GoogleMap
        bootstrapURLKeys={{
          key: apiKey,
        }}
        center={center as { lat: number; lng: number }}
        distanceToMouse={distanceToMouse}
        hoverDistance={25}
        onChange={handleChange}
        onChildClick={handleChildClick}
        onGoogleApiLoaded={handleGoogleApiLoaded}
        options={createOptions}
        resetBoundsOnResize={true}
        yesIWantToUseGoogleMapApiInternals
        zoom={zoom}
      >
        {markers.map((marker, index) =>
          marker === null || !marker.geoPosition ? null : (
            <MapMarker
              events={hashedLocations[marker.formattedAddress]}
              index={index}
              key={index}
              lat={marker.geoPosition.lat}
              lng={marker.geoPosition.lng}
              onCloseClick={() => focusLocation(null)}
              showInfo={index === focusedLocationIndex}
            />
          ),
        )}
      </GoogleMap>
    </div>
  );
};

const MemoizedMap = memo(Map);

export default MemoizedMap;
